import {
  createFormGroupState,
  formGroupReducer,
  FormGroupState,
  onNgrxForms,
  onNgrxFormsAction,
  SetValueAction,
  updateGroup,
  validate,
  wrapReducerWithFormStateUpdate,
} from 'ngrx-forms'
import { createReducer, on } from '@ngrx/store'
import { DebitCharge, PaymentMethodsState } from './user-payment-methods.state'
import { PaymentMethodActions } from './user-payment-methods.actions'
import { PendingState } from '../account.root.state'
import { required, requiredTrue } from 'ngrx-forms/validation'
import { validateIBAN } from 'ngx-iban-validator'
import { IBANError } from 'ngx-iban-validator/dist/iban.validator'

const initialNewDebitChargeState: FormGroupState<Omit<DebitCharge, 'mandateReference'>> = createFormGroupState(
  'new-debit-charge',
  {
    type: 'sepa',
    owner: '',
    iban: '',
    bic: '',
    bank: '',
    sepaAgreement: false,
    sepaConditions: false,
  },
)

const initialEditPaymentMethodState: FormGroupState<DebitCharge> = createFormGroupState('edit-payment-method', {
  type: 'sepa',
  owner: '',
  iban: '',
  bic: '',
  bank: '',
  mandateReference: '',
  sepaAgreement: false,
  sepaConditions: false,
})

export const initialPaymentMethodToEdit: DebitCharge = {
  type: 'sepa',
  owner: '',
  iban: '',
  bic: '',
  bank: '',
  mandateReference: '',
  sepaAgreement: false,
  sepaConditions: false,
}

const initialState: PaymentMethodsState = {
  newDebitChargeControls: initialNewDebitChargeState,
  editPaymentMethodControls: initialEditPaymentMethodState,
  paymentMethodToEdit: initialPaymentMethodToEdit,
  loadedPaymentMethodToEdit: undefined,
}

export const rawPaymentMethodsReducer = createReducer(
  initialState,
  onNgrxForms(),
  onNgrxFormsAction(SetValueAction, (state, action) => ({
    ...state,
    newDebitChargeControls: formGroupReducer(state.newDebitChargeControls, action),
    editPaymentMethodControls: formGroupReducer(state.editPaymentMethodControls, action),
  })),
  on(PaymentMethodActions.setPaymentMethodToEdit, (state, action) => ({
    ...state,
    paymentMethodToEdit: action.payload,
    editPaymentMethodControls: formGroupReducer(
      state.editPaymentMethodControls,
      new SetValueAction<DebitCharge>(state.editPaymentMethodControls.id, action.payload),
    ),
  })),
  on(
    PaymentMethodActions.paymentMethodToEditLoad,
    (state): PaymentMethodsState => ({
      ...state,
      loadedPaymentMethodToEdit: PendingState.pending,
    }),
  ),
  on(
    PaymentMethodActions.paymentMethodToEditSuccess,
    (state): PaymentMethodsState => ({
      ...state,
      loadedPaymentMethodToEdit: PendingState.success,
    }),
  ),
  on(
    PaymentMethodActions.paymentMethodToEditFail,
    (state): PaymentMethodsState => ({
      ...state,
      loadedPaymentMethodToEdit: PendingState.failed,
    }),
  ),
)

export interface MyCustomValidationError extends IBANError {}

export interface IbanValidationErrors {
  ibanError?: MyCustomValidationError
}

function validIban(value: string): IbanValidationErrors {
  let ibanValidationResult = validateIBAN(value)
  return ibanValidationResult?.ibanInvalid ? { ibanError: ibanValidationResult.error } : {}
}

const validateNewDebitChargeControls = updateGroup<Omit<DebitCharge, 'mandateReference'>>({
  owner: validate(required),
  iban: validate(required, validIban),
  sepaAgreement: validate(requiredTrue),
  sepaConditions: validate(requiredTrue),
})

export const paymentMethodsReducer = wrapReducerWithFormStateUpdate(
  rawPaymentMethodsReducer,
  s => s?.newDebitChargeControls,
  validateNewDebitChargeControls,
)
